"use strict";
// Single source of truth for the site's languages and URL shape.
//
// Every page is generated once per locale and lives under a locale prefix
// (/en/privacy, /it/privacy). The prefix is what makes the language visible to
// crawlers: each language gets its own indexable URL, and the pages
// cross-reference each other with rel="alternate" hreflang links.
//
// This module is deliberately dependency-free so it can be consumed by all
// three worlds at once: the client bundle (webpack/ts-loader), the Express
// server (ts-node), and webpack.config.js itself (ts-node/register). Do not
// import anything here that only one of them can resolve.
Object.defineProperty(exports, "__esModule", { value: true });
exports.switchLocale = exports.withLocale = exports.stripLocale = exports.localeFromPath = exports.preferredLocale = exports.isLocale = exports.OG_LOCALES = exports.HREF_LANGS = exports.LANGUAGE_NAMES = exports.indexableLocalesFor = exports.canonicalLocaleFor = exports.translatedLocalesFor = exports.TRANSLATED_LOCALES = exports.CANONICAL_ORIGIN = exports.CANONICAL_HOST = exports.singleLocaleFor = exports.SINGLE_LOCALE_TREES = exports.PRESS_LOCALE = exports.NEWS_LOCALE = exports.DEFAULT_LOCALE = exports.LOCALES = void 0;
exports.LOCALES = ['en', 'it', 'fr', 'es', 'de', 'hi', 'ru', 'pt', 'ja', 'zh', 'ko', 'id'];
// The language served at the bare domain: `/` permanently redirects to
// `/en/`, and any legacy URL without a prefix is redirected into this locale.
exports.DEFAULT_LOCALE = 'en';
// News posts are written in English only (see ARCHITECTURE §4), so the whole
// /news tree exists in exactly one locale. Requests for /<other>/news are
// redirected here rather than serving a half-translated duplicate, which is
// what search engines penalise.
exports.NEWS_LOCALE = 'en';
// The press kit is English-only for the same reason, plus one of its own: it is
// read by journalists and by Apple's editorial team, and it is the page whose
// wording is most likely to be quoted verbatim. A machine-translated product
// description is a worse liability there than an English one.
exports.PRESS_LOCALE = 'en';
/**
 * URL subtrees that exist in exactly one language, mapped to that language.
 *
 * This is the single definition of "which pages are not translated". The build
 * (`localesForPage` in build/localize.js) reads it to decide how many copies of
 * a page to emit and whether to write an hreflang set; `withLocale` below reads
 * it so an unprefixed `/news` link in the shared navigation resolves to a page
 * that exists no matter which locale renders it; and the server reads it to
 * redirect `/it/press` to the one real URL.
 *
 * Adding a single-language page means adding one entry here and nothing else —
 * the previous shape of this file special-cased `/news` in three separate
 * places, which is exactly the kind of drift ARCHITECTURE §10 exists to warn
 * about.
 */
exports.SINGLE_LOCALE_TREES = {
    '/news': exports.NEWS_LOCALE,
    '/press': exports.PRESS_LOCALE,
};
/**
 * The one locale a path is published in, or null when it is a normal page that
 * exists in every language. Matches the subtree root itself and anything under
 * it, so `/news` and `/news/some-post` both resolve.
 */
const singleLocaleFor = (pathname) => {
    for (const [root, locale] of Object.entries(exports.SINGLE_LOCALE_TREES)) {
        if (pathname === root || pathname.startsWith(`${root}/`))
            return locale;
    }
    return null;
};
exports.singleLocaleFor = singleLocaleFor;
/**
 * The canonical origin: the one hostname the site is indexed under.
 *
 * Lives here, next to the rest of the URL logic, so the server (which redirects
 * the bare domain to it) and webpack.config.js (which builds every canonical,
 * hreflang and sitemap URL from it) cannot drift apart. It is still repeated in
 * `src/static/robots.txt` and in the og:image of every page head, neither of
 * which can import TypeScript — grep for `shapely3d.com` before changing it.
 */
exports.CANONICAL_HOST = 'www.shapely3d.com';
exports.CANONICAL_ORIGIN = `https://${exports.CANONICAL_HOST}`;
/**
 * Pages published in every language whose *body copy* is only really translated
 * in some of them, mapped to the locales that do translate it.
 *
 * The legal pages are the case this exists for: `englishLegal` in
 * src/translations.ts deliberately serves the English privacy policy and terms
 * under every locale, because a plausible-looking but subtly wrong GDPR
 * paragraph is a liability rather than a typo (an Italian translation shipped
 * for a while and was removed — re-proof-reading it on every legal edit was
 * not worth it). The pages still have to exist in every locale so the footer
 * links and the language picker never strand anyone — but twelve URLs carrying
 * the same English text, each declaring itself canonical, is a duplicate
 * cluster, and Search Console reports it as exactly that ("Duplicate without
 * user-selected canonical").
 *
 * So the untranslated copies stay reachable and point their canonical tag at
 * the locale the text is actually written in, and only the genuinely translated
 * locales carry the hreflang set and appear in the sitemap.
 *
 * Translating a language's legal text means adding its locale here *and*
 * writing the keys out in its block in src/translations.ts. The two have to
 * move together: this list is a claim about what that file contains.
 */
exports.TRANSLATED_LOCALES = {
    '/privacy': ['en'],
    '/terms': ['en'],
};
/**
 * The locales a path is genuinely translated into, or null for a normal page
 * that is translated everywhere it is published.
 */
const translatedLocalesFor = (pathname) => exports.TRANSLATED_LOCALES[pathname] ?? null;
exports.translatedLocalesFor = translatedLocalesFor;
/**
 * The locale whose copy of a page is the indexable one for `locale`: itself for
 * a real translation, the default locale for a copy that merely repeats the
 * default locale's text.
 */
const canonicalLocaleFor = (pathname, locale) => {
    const translated = (0, exports.translatedLocalesFor)(pathname);
    return !translated || translated.includes(locale) ? locale : exports.DEFAULT_LOCALE;
};
exports.canonicalLocaleFor = canonicalLocaleFor;
/**
 * The locales whose copy of a page is canonical — the set that gets an hreflang
 * entry and a sitemap `<loc>`.
 *
 * Deliberately not the same as how many copies the build emits (that is
 * `localesForPage` in build/localize.js): an untranslated copy is still
 * generated and still served, it just points at the translated one instead of
 * competing with it.
 */
const indexableLocalesFor = (pathname) => {
    const only = (0, exports.singleLocaleFor)(pathname);
    if (only)
        return [only];
    return (0, exports.translatedLocalesFor)(pathname) ?? exports.LOCALES;
};
exports.indexableLocalesFor = indexableLocalesFor;
// Each language is named in itself (the endonym), never in English: a visitor
// who cannot read the current page still has to recognise their own language in
// the picker.
exports.LANGUAGE_NAMES = {
    en: 'English',
    it: 'Italiano',
    fr: 'Français',
    es: 'Español',
    de: 'Deutsch',
    hi: 'हिन्दी',
    ru: 'Русский',
    pt: 'Português',
    ja: '日本語',
    zh: '简体中文',
    ko: '한국어',
    id: 'Bahasa Indonesia',
};
// The `hreflang` value for each locale. Kept separate from the URL prefix so a
// regional variant (e.g. `pt-br`) can have a short prefix and a full tag. This
// is what `zh` uses: the URL stays `/zh/` while the tag is `zh-Hans`, which
// tells search engines the page is Simplified — and leaves `zh-Hant` free for a
// Traditional variant later without changing any existing URL.
exports.HREF_LANGS = {
    en: 'en',
    it: 'it',
    fr: 'fr',
    es: 'es',
    de: 'de',
    hi: 'hi',
    ru: 'ru',
    pt: 'pt',
    ja: 'ja',
    zh: 'zh-Hans',
    ko: 'ko',
    id: 'id',
};
// Open Graph wants a language_TERRITORY tag, unlike hreflang which accepts a
// bare language. Facebook/LinkedIn silently ignore `og:locale` values that
// don't match that shape — and their supported list is territory-based, which
// is why Simplified Chinese is `zh_CN` here but `zh-Hans` above.
exports.OG_LOCALES = {
    en: 'en_US',
    it: 'it_IT',
    fr: 'fr_FR',
    es: 'es_ES',
    de: 'de_DE',
    hi: 'hi_IN',
    ru: 'ru_RU',
    pt: 'pt_PT',
    ja: 'ja_JP',
    zh: 'zh_CN',
    ko: 'ko_KR',
    id: 'id_ID',
};
const isLocale = (value) => exports.LOCALES.includes(value);
exports.isLocale = isLocale;
/**
 * The best supported language for a visitor, from an `Accept-Language` header
 * (server) or something shaped like one, e.g. `navigator.languages.join(',')`
 * (browser). Falls back to DEFAULT_LOCALE when nothing matches.
 *
 * This is only ever used to pick a destination for the bare domain, which has
 * no language of its own. It must NOT be used to redirect a locale-prefixed
 * URL: sending a visitor — or a crawler — away from the URL they asked for
 * because of their device settings is what breaks per-language indexing, and
 * it would make `/it/privacy` unreachable from an English-configured machine.
 *
 * Entries are walked in descending quality order and each is tried as an exact
 * tag first, then as its base language — so `it-CH` resolves to `it` and
 * `zh-Hans-CN` to `zh`, while a future `pt-br` locale would still beat a plain
 * `pt` listed after it. Doing the exact pass across the whole list first would
 * be wrong: `it-IT,en;q=0.5` would match `en` exactly and hand an Italian
 * visitor the English site.
 *
 * `*` — which means "anything" — is skipped so it doesn't outrank a real
 * preference, and `q=0` ("not acceptable") entries are dropped.
 */
const preferredLocale = (acceptLanguage) => {
    if (!acceptLanguage)
        return exports.DEFAULT_LOCALE;
    const entries = acceptLanguage
        .split(',')
        .map((part) => {
        const [tag = '', ...params] = part.trim().split(';');
        // Quality defaults to 1 when the client omits `;q=`. An unparseable or
        // out-of-range value is treated as 0 — the spec's "not acceptable".
        const qParam = params.find((param) => param.trim().startsWith('q='));
        const q = qParam ? Number.parseFloat(qParam.trim().slice(2)) : 1;
        return { tag: tag.trim().toLowerCase(), q: Number.isFinite(q) && q >= 0 && q <= 1 ? q : 0 };
    })
        // A stable sort by descending quality keeps the header's own order as the
        // tie-breaker, which is what clients mean by listing languages unweighted.
        .filter((entry) => entry.tag && entry.tag !== '*' && entry.q > 0)
        .sort((a, b) => b.q - a.q);
    for (const entry of entries) {
        if ((0, exports.isLocale)(entry.tag))
            return entry.tag;
        const base = entry.tag.split('-')[0] ?? '';
        if ((0, exports.isLocale)(base))
            return base;
    }
    return exports.DEFAULT_LOCALE;
};
exports.preferredLocale = preferredLocale;
/**
 * The locale a pathname belongs to, or null when it carries no locale prefix
 * (an asset, a legacy URL, or the bare root).
 */
const localeFromPath = (pathname) => {
    const first = pathname.split('/')[1] ?? '';
    return (0, exports.isLocale)(first) ? first : null;
};
exports.localeFromPath = localeFromPath;
/**
 * The same pathname with its locale prefix removed, always starting with `/`.
 * `/it/privacy` → `/privacy`, `/en/` → `/`.
 */
const stripLocale = (pathname) => {
    const locale = (0, exports.localeFromPath)(pathname);
    if (!locale)
        return pathname;
    const rest = pathname.slice(locale.length + 1);
    return rest.startsWith('/') ? rest : `/${rest}`;
};
exports.stripLocale = stripLocale;
/**
 * Build a locale-prefixed URL from an unprefixed path.
 *
 * A single-language subtree (see SINGLE_LOCALE_TREES) always resolves to its
 * own locale regardless of the requested language, so a visitor browsing in
 * Italian who clicks "News" or "Press Kit" lands on a real page instead of a
 * 404.
 */
const withLocale = (pathname, locale) => {
    const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
    const target = (0, exports.singleLocaleFor)(path) ?? locale;
    // The landing page keeps its trailing slash (`/it/`) so the canonical URL of
    // a directory-style page and the URL the server redirects to are identical.
    if (path === '/')
        return `/${target}/`;
    return `/${target}${path}`;
};
exports.withLocale = withLocale;
/**
 * The equivalent of the current pathname in another language — what the
 * language picker navigates to, and what the hreflang alternates point at.
 */
const switchLocale = (pathname, locale) => (0, exports.withLocale)((0, exports.stripLocale)(pathname) || '/', locale);
exports.switchLocale = switchLocale;
